import { OfferSource } from '@cds';
import {
    DisplayType,
    Game,
    GroupingInfo,
    OptionMarket,
    OrderType,
    Parameter,
    ParameterType,
    Participant,
    ParticipantType,
    Translation,
} from '@cds/betting-offer';
import {
    EnhancedFixtureViewGroupingConfiguration,
    EventOption,
    EventOptionGroup,
    ExtendedDisplayType,
    HappeningValue,
    MarketTypes,
    SortOrder,
    Tv2MarketParameters,
} from '@frontend/sports/betting-offer/feature/model';
import { HappeningType, MarketType } from '@frontend/sports/betting-offer/feature/types';
import { BetBuilderConfig } from '@frontend/sports/common/client-config-data-access';
import { interpolateSingleBracesString } from '@frontend/sports/common/core/utils/string';
import { groupBy, isEmpty, orderBy } from 'lodash-es';

import { getOptionImage, getParticipantImageMap } from '../helpers/participant-image.helper';

export abstract class CommonMarketFactory {
    private readonly OVER_UNDER_MATCHER = /(.+ )(\d+([.,]\d+)?)$/;
    private readonly OPTION_ATTRIBUTE_MATCHER = /(^|\s|[()])([-+]?(?:\d+(?:[.,]\d+)?|[.,]\d+))(?=\s|$|[()])/;

    constructor(private betbuilderConfig: BetBuilderConfig) {}

    protected getGridGroups(grouping: GroupingInfo, categoryId?: number): string[] {
        let groups: string[] = [];
        if (grouping && !isEmpty(grouping.gridGroups)) {
            groups = grouping.gridGroups;
        }
        if (!groups.length && categoryId !== undefined) {
            groups = [categoryId.toString()];
        }

        return groups;
    }

    protected patch(
        market: EventOptionGroup,
        originalMarket: Game | OptionMarket,
        participants?: Participant[],
        excludePriceboostedMarketGrouping?: boolean,
    ) {
        if (originalMarket.templateCategory?.dynamicCategories?.length) {
            // eslint-disable-next-line no-param-reassign
            market.dynamicCategories = originalMarket.templateCategory.dynamicCategories;
        }

        if ('parameters' in originalMarket) {
            const marketType = originalMarket.parameters.find((param) => param.key === Tv2MarketParameters.MarketType);

            // market type returned for virtual fixtures and we mock the params
            if (marketType) {
                switch (marketType.value) {
                    case MarketTypes.Spread:
                        return this.patchSpread(market);
                    case MarketTypes.CorrectScore:
                        return this.patchCorrectScore(market);
                    case MarketTypes.BetBuilder:
                        return this.patchPreCreatedBetBuilder(market);
                    case MarketTypes.OverUnder:
                    case MarketTypes.TotalsOverUnder:
                        return this.patchOverUnder(market);
                    case MarketTypes.HeadToHead:
                    case MarketTypes.TwoWayHandicap:
                        return this.patchHeadToHead(market, participants);
                    case MarketTypes.AutomatedPriceBoost:
                        return this.patchAutomatedPriceBoost(market, excludePriceboostedMarketGrouping);
                }
            } else if (market.source === OfferSource.V2 && this.isOverUnder(market)) {
                return this.patchOverUnder(market);
            }
        }

        if (market.isPartOfSgpGroup) {
            this.patchPreCreatedBetBuilder(market);
        }
    }

    private getTranslated_Value(translation: Translation | undefined): string {
        return translation?.short || translation?.value || '';
    }

    private patchAutomatedPriceBoost(market: EventOptionGroup, excludePriceboostedMarketGrouping?: boolean): void {
        // eslint-disable-next-line no-param-reassign
        market.detailedGrouping = {
            displayType: ExtendedDisplayType.AutomatedPriceBoost,
            orderType: OrderType.None,
            marketSet: market.detailedGrouping?.marketSet ?? 0,
            marketOrder: market.detailedGrouping?.marketOrder ?? 0,
            marketGroupId: market.detailedGrouping?.marketGroupId ?? '',
            marketGroupItemId: market.detailedGrouping?.marketGroupItemId ?? '',
            marketGroup: !excludePriceboostedMarketGrouping ? Number.MAX_VALUE : market.detailedGrouping?.marketGroup,
        };
        market.options.forEach((option) => {
            // eslint-disable-next-line no-param-reassign
            option.showBoostedPrice = true;
            // eslint-disable-next-line no-param-reassign
            option.optionMarketName = market.name;
        });
        // eslint-disable-next-line no-param-reassign
        market.balanced = true;
    }

    // TODO please refactor below function if you are modifying it, currently it's unreadable, any further changes will make it just worse.
    // eslint-disable-next-line complexity
    protected patchDetailedGrouping(
        market: EventOptionGroup,
        originalMarket: Game | OptionMarket,
        grouping: EnhancedFixtureViewGroupingConfiguration,
        participants: Participant[] | undefined,
        correctScoreOptionsSortOrder: string | undefined,
    ) {
        if ('parameters' in originalMarket) {
            const marketType = originalMarket.parameters.find((param) => param.key === Tv2MarketParameters.MarketType);
            const happening = originalMarket.parameters.find((param) => param.key === Tv2MarketParameters.Happening);
            const subPeriodNumber = originalMarket.parameters.find((param) => param.key === Tv2MarketParameters.SubPeriodNumber);
            const rangeValue = originalMarket.parameters.find((param) => param.key === Tv2MarketParameters.RangeValue);

            if (
                marketType &&
                happening &&
                market.detailedGrouping &&
                (marketType.value === MarketTypes.HeadToHead || marketType.value === MarketTypes.TwoWayHandicap) &&
                market.detailedGrouping.displayType !== DisplayType.SixPack
            ) {
                this.patchHeadToHeadMarketName(market, happening, grouping);
                if (participants) {
                    // eslint-disable-next-line no-param-reassign
                    market.options = this.patchParticipantIdToOption(market, participants);
                }
            } else if (marketType?.value === MarketTypes.Scorer && participants) {
                // eslint-disable-next-line no-param-reassign
                market.options = this.patchParticipantIdToOption(market, participants);
            } else if (marketType?.value === MarketTypes.MultiFPOverUnder && participants) {
                this.patchPlayerCombined(market, originalMarket, participants);
            } else if (
                market.detailedGrouping?.displayType === DisplayType.PlayerMilestone ||
                market.detailedGrouping?.displayType === DisplayType.PlayerMilestoneOverUnder
            ) {
                this.patchPlayerMilestone(market, originalMarket, marketType, happening, participants, grouping);
            } else if (market.detailedGrouping?.displayType === DisplayType.PlayerOutcome) {
                this.patchPlayerOutcome(market, originalMarket, marketType, happening, subPeriodNumber);
            } else if (marketType?.value === MarketTypes.DynamicCorrectScore && happening?.value === HappeningValue.Points && participants) {
                this.patchCorrectScoreOptions(market, participants, correctScoreOptionsSortOrder);
            } else if (marketType && !!subPeriodNumber?.value) {
                // eslint-disable-next-line no-param-reassign
                market.subPeriodNumber = subPeriodNumber?.value;
            } else if (marketType && rangeValue) {
                // eslint-disable-next-line no-param-reassign
                market.rangeValue = rangeValue.value;
            }
        }
    }

    protected extractOptionAttribute(optionName: string): string | undefined {
        const match = optionName.match(this.OPTION_ATTRIBUTE_MATCHER);

        if (!match) {
            return;
        }

        // match[0] = full match with boundaries, match[1] = boundary char, match[2] = pure number
        return match[2][0] === '-' || match[2][0] === '+' ? match[2] : `+${match[2]}`;
    }

    private patchPlayerOutcome(
        market: EventOptionGroup,
        originalMarket: OptionMarket,
        marketType: Parameter | undefined,
        happening: Parameter | undefined,
        subPeriodNumber: Parameter | undefined,
    ): void {
        // eslint-disable-next-line no-param-reassign
        market.options = orderBy(market.options, (option) => option.id);
        const subPeriod = originalMarket.parameters.find((param) => param.key === Tv2MarketParameters.SubPeriod);
        if (subPeriod && subPeriodNumber && marketType?.value === 'Over/Under' && happening?.value === 'Pitch') {
            // eslint-disable-next-line no-param-reassign
            market.subPeriodNumber = subPeriod.value + subPeriodNumber.value;
        }
    }

    private patchCorrectScoreOptions(market: EventOptionGroup, participants: Participant[], correctScoreOptionsSortOrder: string | undefined): void {
        const homeTeam = this.getTranslated_Value(participants.find((p) => p.properties?.type === ParticipantType.HomeTeam)?.name);
        const awayTeam = this.getTranslated_Value(participants.find((p) => p.properties?.type === ParticipantType.AwayTeam)?.name);
        if (!homeTeam || !awayTeam) {
            return;
        }
        if (
            !!correctScoreOptionsSortOrder &&
            (correctScoreOptionsSortOrder === SortOrder.Ascending || correctScoreOptionsSortOrder === SortOrder.Descending)
        ) {
            let correctScoreOptions: { option: EventOption; rawScore: string }[] = [];
            correctScoreOptions = market.options.map((option) => {
                const optionNameValues = option?.name?.split(':');
                // eslint-disable-next-line no-param-reassign
                option.name = `${homeTeam} ${optionNameValues[0]} - ${awayTeam} ${optionNameValues[1]}`;
                const rawScore = `${optionNameValues[0].padStart(3, '0')}:${optionNameValues[1].padStart(3, '0')}`;

                return { option, rawScore };
            });
            correctScoreOptions = orderBy(correctScoreOptions, (option) => option.rawScore, correctScoreOptionsSortOrder);
            // eslint-disable-next-line no-param-reassign
            market.options = correctScoreOptions.map((correctScoreOption) => correctScoreOption.option);
        } else {
            // eslint-disable-next-line no-param-reassign
            market.options = market.options.map((option) => {
                const optionNameValues = option?.name?.split(':');
                // eslint-disable-next-line no-param-reassign
                option.name = `${homeTeam} ${optionNameValues[0]} - ${awayTeam} ${optionNameValues[1]}`;

                return option;
            });
        }
    }

    private patchPlayerMilestone(
        market: EventOptionGroup,
        originalMarket: OptionMarket,
        marketType: Parameter | undefined,
        happening: Parameter | undefined,
        participants: Participant[] | undefined,
        grouping: EnhancedFixtureViewGroupingConfiguration,
    ): void {
        const participantId = originalMarket.parameters.find((param) => param.key === Tv2MarketParameters.FixtureParticipant)?.value;
        const happeningIndex = grouping?.masterSorting['happening']?.findIndex((s) => s === happening?.value);
        // eslint-disable-next-line no-param-reassign
        market.subPeriodNumber = happeningIndex?.toString();
        // eslint-disable-next-line no-param-reassign
        market.options = orderBy(market.options, (option) => option.id);
        if (participantId) {
            const participant = participants?.find((p) => p.id === +participantId);
            // eslint-disable-next-line no-param-reassign
            market.options = market.options.map((option) => {
                // eslint-disable-next-line no-param-reassign
                option.player1Id = participant?.participantId;

                return option;
            });
        }
        if (marketType && (marketType.value === MarketTypes.HappeningToOccurV2 || marketType.value === MarketTypes.HappeningToOccurAndTeamToWin)) {
            // eslint-disable-next-line no-param-reassign
            market.detailedGrouping.marketGroup = happeningIndex;
        }
    }

    private patchPlayerCombined(market: EventOptionGroup, originalMarket: OptionMarket, participants: Participant[]): void {
        const participantsList = originalMarket.parameters.find((param) => param.key === Tv2MarketParameters.FixtureParticipantList);
        const participantImageMap = getParticipantImageMap(participants);
        // eslint-disable-next-line no-param-reassign
        market.options = orderBy(market.options, (option) => option.id);
        if (!participantsList) return;
        const list = participantsList.value.split(',');
        let player1Id: number | undefined;
        let player2Id: number | undefined;
        if (list.length === 2) {
            player1Id = participants.find((p) => p.id === +list[0])?.participantId;
            player2Id = participants.find((p) => p.id === +list[1])?.participantId;
        }
        // eslint-disable-next-line no-param-reassign
        market.options = market.options.map((option, index) => {
            const name = participants.find((p) => p.id === +list[index])?.name;
            // eslint-disable-next-line no-param-reassign
            option.name = this.getTranslated_Value(name) || option.name;
            // eslint-disable-next-line no-param-reassign
            option.player1Id = player1Id;
            // eslint-disable-next-line no-param-reassign
            option.player2Id = player2Id;
            // eslint-disable-next-line no-param-reassign
            option.image = getOptionImage(participants, option, participantImageMap);
            return option;
        });
    }

    private patchHeadToHeadMarketName(market: EventOptionGroup, happening: Parameter, grouping: EnhancedFixtureViewGroupingConfiguration): void {
        const happeningIndex = grouping?.masterSorting['happening']?.findIndex((s) => s === happening?.value);
        // eslint-disable-next-line no-param-reassign
        market.detailedGrouping.marketGroup = happeningIndex;

        if (
            market.detailedGrouping.displayType === DisplayType.Regular &&
            (happening.value === HappeningValue.PassingYards || happening.value === HappeningValue.TouchdownPass)
        ) {
            // eslint-disable-next-line no-param-reassign
            market.participantIds = undefined;
        }

        if (market.detailedGrouping.name) {
            const interpolatedName = interpolateSingleBracesString(market.detailedGrouping.name, {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                Player1: market.player1!,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                Player2: market.player2!,
            });
            // eslint-disable-next-line no-param-reassign
            market.detailedGrouping.name = interpolatedName;
            // eslint-disable-next-line no-param-reassign
            market.name = interpolatedName;
        }
    }

    private patchParticipantIdToOption(market: EventOptionGroup, participants: Participant[]): EventOption[] {
        return market.options.map((option) => {
            // eslint-disable-next-line no-param-reassign
            option.player1Id = participants.find((p) => p.id === option.optionPlayerId)?.participantId;

            return option;
        });
    }

    private patchHeadToHead(market: EventOptionGroup, participants?: Participant[]): void {
        const participantIds: number[] = [];
        // eslint-disable-next-line no-param-reassign
        market.options = orderBy(market.options, (option) => option.id);
        market.options.forEach((option, index) => {
            const playerName = participants?.find((p) => p.id === option.optionPlayerId)?.name.value || option.name;
            if (index === 0) {
                // eslint-disable-next-line no-param-reassign
                market.player1 = playerName;
            } else {
                // eslint-disable-next-line no-param-reassign
                market.player2 = playerName;
            }
            // eslint-disable-next-line no-param-reassign
            option.marketAttribute = market.name;
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            participantIds.push(option.optionPlayerId!);
        });

        // eslint-disable-next-line no-param-reassign
        market.participantIds = participantIds.reduce((current, next) => current + next.toString(), '_');
    }

    private patchSpread(market: EventOptionGroup): void {
        // eslint-disable-next-line no-param-reassign
        market.balanced = true;
    }

    private patchCorrectScore(market: EventOptionGroup): void {
        const happening = { key: Tv2MarketParameters.Happening, type: ParameterType.String, value: HappeningType.Goal };
        const marketType = {
            key: Tv2MarketParameters.MarketType,
            type: ParameterType.String,
            value: MarketType.CorrectScore,
        };

        this.addParameterToMarket(market, happening);
        this.addParameterToMarket(market, marketType);

        // eslint-disable-next-line no-param-reassign
        market.detailedGrouping = {
            displayType: DisplayType.CorrectScore,
            orderType: OrderType.None,
            marketSet: 0,
            marketOrder: 0,
            marketGroupId: market.detailedGrouping?.marketGroupId || '',
            marketGroupItemId: market.detailedGrouping?.marketGroupItemId || '',
        };
    }

    private addParameterToMarket(market: EventOptionGroup, parameter: Parameter): void {
        const existingParameter = market.parameters?.find((param) => param.key === parameter.key);

        if (existingParameter) {
            existingParameter.value = parameter.value;
        } else {
            market.parameters?.push(parameter);
        }
    }

    private patchPreCreatedBetBuilder(market: EventOptionGroup): void {
        if (market.detailedGrouping?.marketTabId) {
            // eslint-disable-next-line no-param-reassign
            market.detailedGrouping = {
                periodGroup: market.detailedGrouping.periodGroup,
                marketOrder: market.detailedGrouping.marketOrder,
                marketGroup: market.detailedGrouping.marketGroup,
                displayType: ExtendedDisplayType.BetBuilder,
                orderType: OrderType.None,
                marketSet: this.betbuilderConfig.enablePrecreatedBetBuilderTab ? market.detailedGrouping.marketSet : 0,
                name: market.detailedGrouping.name,
                marketHelpPath: market.detailedGrouping.marketHelpPath,
                marketGroupId: market.detailedGrouping.marketGroupId,
                marketGroupItemId: market.detailedGrouping.marketGroupItemId,
                marketTabId: market.detailedGrouping.marketTabId,
                //TODO try splitting this file!
                // eslint-disable-next-line max-lines
                precreatedGroupId: market.detailedGrouping.precreatedGroupId,
            };
            // eslint-disable-next-line max-lines
        }
    }

    private patchOverUnder(market: EventOptionGroup): void {
        const marketType = {
            key: Tv2MarketParameters.MarketType,
            type: ParameterType.String,
            value: MarketType.OverUnder,
        };

        this.addParameterToMarket(market, marketType);

        // eslint-disable-next-line no-param-reassign
        market.parameterTranslations = market.parameterTranslations || {};
        // eslint-disable-next-line no-param-reassign
        market.detailedGrouping = {
            displayType: DisplayType.OverUnder,
            orderType: market.detailedGrouping?.orderType ?? OrderType.None,
            marketSet: 0,
            marketOrder: 0,
            marketGroupId: market.detailedGrouping?.marketGroupId || '',
            marketGroupItemId: market.detailedGrouping?.marketGroupItemId || '',
        };

        market.options.forEach((option, index) => {
            const match = option.name.match(this.OVER_UNDER_MATCHER);

            if (match) {
                if (index === 0) {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, no-param-reassign
                    market.parameterTranslations!['columnOver'] = match[1].trim();
                }

                if (index === 1) {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, no-param-reassign
                    market.parameterTranslations!['columnUnder'] = match[1].trim();
                }
                // eslint-disable-next-line no-param-reassign
                option.marketAttribute = match[2];
            }
        });
    }

    private isOverUnder(market: EventOptionGroup): boolean {
        const attributes: string[] = [];

        for (const option of market.options) {
            const match = option.name.match(this.OVER_UNDER_MATCHER);

            if (!match) {
                return false;
            }

            attributes.push(match[2]);
        }

        if (attributes.length) {
            const pairs = groupBy(attributes, (group) => group);

            return Object.keys(pairs).every((attribute) => pairs[attribute].length === 2);
        }

        return false;
    }
}
